import React, { useMemo, useState } from 'react';
import {
  ArrowDownUp,
  ArrowDown,
  ArrowUp,
  Pencil,
  Trash2,
  ChevronRight,
  Users,
  Mail,
  Phone,
} from 'lucide-react';
import { Customer } from '../types';
import { Avatar, Badge, Card, IconButton, loyaltyTone } from './ui';

interface CustomerListProps {
  customers: Customer[];
  onSelectCustomer: (id: string) => void;
  onEditCustomer: (customer: Customer) => void;
  onDeleteCustomer: (id: string) => void;
  selectedCustomerId: string | null;
}

type SortKey = 'name' | 'totalSpent' | 'bookings';
type SortDir = 'asc' | 'desc';

const CustomerList: React.FC<CustomerListProps> = ({
  customers,
  onSelectCustomer,
  onEditCustomer,
  onDeleteCustomer,
  selectedCustomerId,
}) => {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: 'name',
    dir: 'asc',
  });

  const sorted = useMemo(() => {
    const arr = [...customers];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name);
      else if (sort.key === 'totalSpent')
        cmp = Number(a.totalSpent) - Number(b.totalSpent);
      else if (sort.key === 'bookings')
        cmp = (a.bookings?.length || 0) - (b.bookings?.length || 0);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [customers, sort]);

  const toggle = (key: SortKey) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );

  const sortIcon = (key: SortKey) => {
    if (sort.key !== key) return <ArrowDownUp className="h-3 w-3 opacity-50" />;
    return sort.dir === 'asc' ? (
      <ArrowUp className="h-3 w-3" />
    ) : (
      <ArrowDown className="h-3 w-3" />
    );
  };

  return (
    <div className="p-3 md:p-6">
      <Card className="overflow-hidden">
        {/* Desktop column header */}
        <div className="hidden md:grid md:grid-cols-[1.5fr_1.4fr_0.8fr_0.9fr_0.6fr_0.5fr] items-center px-5 py-3 border-b border-sand-200 bg-sand-50/60 text-[11px] font-medium text-sand-600 uppercase tracking-wider">
          <button
            onClick={() => toggle('name')}
            className="flex items-center gap-1.5 text-left hover:text-sand-900"
          >
            Customer {sortIcon('name')}
          </button>
          <span>Contact</span>
          <span>Status</span>
          <button
            onClick={() => toggle('totalSpent')}
            className="flex items-center gap-1.5 text-left hover:text-sand-900"
          >
            Total spent {sortIcon('totalSpent')}
          </button>
          <button
            onClick={() => toggle('bookings')}
            className="flex items-center gap-1.5 text-left hover:text-sand-900"
          >
            Bookings {sortIcon('bookings')}
          </button>
          <span className="text-right">Actions</span>
        </div>

        {/* Mobile sort chips */}
        <div className="md:hidden flex items-center gap-2 px-4 py-2.5 border-b border-sand-200 bg-sand-50/60 overflow-x-auto">
          <span className="text-[11px] font-medium text-sand-500 uppercase tracking-wider mr-1">
            Sort
          </span>
          {(
            [
              { k: 'name' as SortKey, label: 'Name' },
              { k: 'totalSpent' as SortKey, label: 'Spend' },
              { k: 'bookings' as SortKey, label: 'Bookings' },
            ]
          ).map(({ k, label }) => (
            <button
              key={k}
              onClick={() => toggle(k)}
              className={[
                'inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors',
                sort.key === k
                  ? 'bg-sage-50 border-sage-200 text-sage-800'
                  : 'bg-white border-sand-200 text-sand-700',
              ].join(' ')}
            >
              {label}
              {sortIcon(k)}
            </button>
          ))}
        </div>

        <ul className="divide-y divide-sand-100">
          {sorted.map((customer) => {
            const selected = selectedCustomerId === customer.id;
            return (
              <li
                key={customer.id}
                className={[
                  'group cursor-pointer transition-colors',
                  selected ? 'bg-sage-50/60' : 'hover:bg-sand-50',
                ].join(' ')}
                onClick={() => onSelectCustomer(customer.id)}
              >
                {/* Desktop row */}
                <div className="hidden md:grid md:grid-cols-[1.5fr_1.4fr_0.8fr_0.9fr_0.6fr_0.5fr] md:items-center gap-2 px-5 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={customer.name} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-sand-900 truncate">
                        {customer.name}
                      </p>
                      <p className="text-[11px] text-sand-400 font-mono truncate">
                        {customer.id.substring(0, 8)}…
                      </p>
                    </div>
                  </div>
                  <div className="min-w-0 text-sm">
                    <p className="text-sand-800 flex items-center gap-1.5 truncate">
                      <Mail className="h-3.5 w-3.5 text-sand-400 shrink-0" />
                      <span className="truncate">{customer.email}</span>
                    </p>
                    {customer.phone && (
                      <p className="text-sand-500 text-xs flex items-center gap-1.5 truncate mt-0.5">
                        <Phone className="h-3 w-3 shrink-0" />
                        <span className="truncate">{customer.phone}</span>
                      </p>
                    )}
                  </div>
                  <div>
                    <Badge tone={loyaltyTone(customer.loyaltyStatus)} uppercase>
                      {customer.loyaltyStatus}
                    </Badge>
                  </div>
                  <div className="text-sm font-medium text-sand-900">
                    £{Number(customer.totalSpent).toLocaleString('en-GB')}
                  </div>
                  <div className="text-sm text-sand-700">
                    {customer.bookings?.length || 0}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <IconButton
                      label="Edit customer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditCustomer(customer);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </IconButton>
                    <IconButton
                      label="Delete customer"
                      variant="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteCustomer(customer.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </IconButton>
                    <ChevronRight className="h-4 w-4 text-sand-300 group-hover:text-sand-500 transition-colors" />
                  </div>
                </div>

                {/* Mobile row */}
                <div className="md:hidden px-4 py-3 flex items-start gap-3">
                  <Avatar name={customer.name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-sm font-medium text-sand-900 truncate">
                        {customer.name}
                      </p>
                      <Badge
                        tone={loyaltyTone(customer.loyaltyStatus)}
                        uppercase
                      >
                        {customer.loyaltyStatus}
                      </Badge>
                    </div>
                    <p className="text-xs text-sand-700 truncate">
                      {customer.email}
                    </p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-sand-500">
                        {customer.bookings?.length || 0} bookings
                      </span>
                      <span className="text-sm font-medium text-sand-900">
                        £{Number(customer.totalSpent).toLocaleString('en-GB')}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-sand-300 shrink-0 mt-1" />
                </div>
              </li>
            );
          })}
        </ul>

        {customers.length === 0 && (
          <div className="py-12 md:py-16 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-sand-100 text-sand-500 mb-3">
              <Users className="h-5 w-5" />
            </div>
            <p className="text-sand-700 font-medium">No customers found</p>
            <p className="text-sand-500 text-sm mt-1">
              Add a customer or refine your search.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CustomerList;
